import { useEffect, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'

import BatchDetailsGrid from 'components/bookingInvoiceBatchDetails/BatchDetailsGrid'
import { FplDataGrid } from 'components/common'
import { useRenewAllBatchesMutation, useSendBatchesToXeroMutation } from 'generated/graphql'
import {
  GET_BOOKING_INVOICE_BATCHES,
  GET_BOOKING_INVOICE_BATCHES_SENT_STATE,
} from 'graphql/queries'
import { useGetInvoiceBatchesColummns } from 'hooks/accountsPayable/useGetInvoiceBatchesColumns'
import { usePermission } from 'providers'

import { useInvoiceBatchFilter } from './hooks/useInvoiceBatchFilter'
import SendToXeroDialog from './SendToXeroDialog'

const InvoiceBatches = () => {
  const [isAllowedToRenewBatches] = usePermission('RenewBatches')
  const [isAllowedToSendBatches] = usePermission('SendBatch')
  const [selectedBatchRow, setSelectedBatchRow] = useState<null | number>(null)
  const [selectedBatchIds, setSelectedBatchIds] = useState<Array<string>>([])
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [queuedBatchIds, setQueuedBatchIds] = useState<Array<string>>([])
  const [lastQueuedIds, setLastQueuedIds] = useState<Array<string>>([])
  const [selectionOverMultiplePages, setSelectionOverMultiplePages] = useState<boolean>(true)
  const [checkingSentState, setCheckingSentState] = useState(false)

  const client = useApolloClient()

  const [renewAllBatches, { loading: renewingAllBatches }] = useRenewAllBatchesMutation()
  const [sendBatchesToXero, { loading: sendingBatches }] = useSendBatchesToXeroMutation()

  const invoiceBatchFilter = useInvoiceBatchFilter()

  // A new filter can exclude selected batches, so the selection must not carry over.
  useEffect(() => {
    setSelectedBatchIds([])
  }, [invoiceBatchFilter])

  const handleSelectionOverMultiplePagesChange = () => {
    if (selectionOverMultiplePages) {
      setSelectedBatchIds([])
    }

    setSelectionOverMultiplePages(!selectionOverMultiplePages)
  }

  const handleRowClickAllGrid = (row) => {
    setSelectedBatchRow(row.id)
  }

  const handleRenewAllBatchesClick = () => renewAllBatches()

  // Batches selected on other pages are not on screen, so their latest sent state is checked first.
  const getSendableBatchIds = async (batchIds: Array<string>) => {
    const { data } = await client.query({
      query: GET_BOOKING_INVOICE_BATCHES_SENT_STATE,
      fetchPolicy: 'network-only',
      variables: { ids: batchIds.map((id) => Number(id)), first: batchIds.length },
    })

    const sentBatchIds = (data?.bookingInvoiceBatches?.edges ?? [])
      .filter((edge) => edge.node.isSentToXero)
      .map((edge) => String(edge.node.id))

    return batchIds.filter((id) => !sentBatchIds.includes(id) && !queuedBatchIds.includes(id))
  }

  const handleSendBatchesToXeroClick = async () => {
    const selectedIds = [...selectedBatchIds]

    setCheckingSentState(true)

    let idsBeingSent: Array<string>
    try {
      idsBeingSent = await getSendableBatchIds(selectedIds)
    } catch {
      toast.error('Could not check the selected batches. Please try again.')
      return
    } finally {
      setCheckingSentState(false)
    }

    const skippedCount = selectedIds.length - idsBeingSent.length

    if (idsBeingSent.length === 0) {
      setSelectedBatchIds([])
      toast.warning('The selected batches have already been sent or queued for Xero.')
      return
    }

    if (skippedCount > 0) {
      toast.warning(`${skippedCount} batch(es) skipped because they were already sent or queued.`)
    }

    sendBatchesToXero({
      variables: {
        batchIds: idsBeingSent.map((i) => Number(i)),
      },
      onCompleted: () => {
        setLastQueuedIds(idsBeingSent)
        setQueuedBatchIds((prev) => [...new Set([...prev, ...idsBeingSent])])
        setSelectedBatchIds([])
        setSendDialogOpen(true)
      },
    })
  }

  const columns = useGetInvoiceBatchesColummns()

  const leftSide = (
    <>
      {isAllowedToRenewBatches && (
        <Grid item>
          <LoadingButton
            variant='outlined'
            onClick={handleRenewAllBatchesClick}
            loading={renewingAllBatches}>
            Renew All
          </LoadingButton>
        </Grid>
      )}

      {isAllowedToSendBatches && (
        <>
          <Grid item>
            <LoadingButton
              variant='outlined'
              disabled={selectedBatchIds.length === 0}
              onClick={handleSendBatchesToXeroClick}
              loading={sendingBatches || checkingSentState}>
              Send to Xero
            </LoadingButton>
          </Grid>

          <Grid item>
            <Typography variant='body2' color='text.secondary'>
              {selectedBatchIds.length} selected
            </Typography>
          </Grid>

          <Grid item>
            <Tooltip
              title={'Use this option if you want to select records across multiple pages. Disabling this option will reset the selection entirely.'}
              arrow>
              <span>
                <FormControlLabel
                  sx={{ whiteSpace: 'nowrap' }}
                  onChange={() => handleSelectionOverMultiplePagesChange()}
                  control={<Switch checked={selectionOverMultiplePages} />}
                  label='Keep selection over pages'
                />
              </span>
            </Tooltip>
          </Grid>
        </>
      )}
    </>
  )

  return (
    <Box>
      {!selectedBatchRow && (
        <FplDataGrid
          query={GET_BOOKING_INVOICE_BATCHES}
          refetchWithDelay
          filter={invoiceBatchFilter}
          checkboxSelection
          entityName='bookingInvoiceBatches'
          toolbar={{
            caption: 'Invoice Batches',
            leftSide: isAllowedToRenewBatches || isAllowedToSendBatches ? leftSide : undefined,
          }}
          columns={columns}
          selectionModel={selectedBatchIds}
          keepNonExistentRowsSelected={selectionOverMultiplePages}
          isRowSelectable={(params) =>
            !params.row.isSentToXero && !queuedBatchIds.includes(String(params.row.id))
          }
          onRowClick={(e) => handleRowClickAllGrid(e)}
          onSelectionModelChange={(e) => setSelectedBatchIds(e as string[])}
        />
      )}

      {selectedBatchRow && (
        <>
          <Button sx={{ marginRight: '15px' }} onClick={() => setSelectedBatchRow(null)}>
            <ArrowBackIcon color='primary'></ArrowBackIcon>
            <Typography color={'primary'}>Back</Typography>
          </Button>

          <BatchDetailsGrid batchId={selectedBatchRow} />
        </>
      )}

      <SendToXeroDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        queuedIds={lastQueuedIds}
      />
    </Box>
  )
}

export default InvoiceBatches